// Dintass · "Comprados frecuentemente juntos": suma los productos marcados y los agrega al carro
// de una vez. Usa los mismos eventos que el formulario de producto de Horizon para que el carro
// (drawer) se actualice y se abra solo.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const formatMoney = (cents) => '$' + Math.round(cents / 100).toLocaleString('es-CL');

class DtCompradosJuntos extends HTMLElement {
  connectedCallback() {
    this.boxes = [...this.querySelectorAll('input.dt-fbt__check')];
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.countEl = this.querySelector('[data-fbt-count]');
    this.button = this.querySelector('[data-fbt-add]');
    this.msg = this.querySelector('[data-fbt-msg]');
    this.boxes.forEach((b) => b.addEventListener('change', () => this.update()));
    this.button?.addEventListener('click', () => this.add());
    this.update();
  }

  get selected() {
    return this.boxes.filter((b) => b.checked);
  }

  update() {
    const sel = this.selected;
    const cents = sel.reduce((s, b) => s + Number(b.dataset.price || 0), 0);
    if (this.totalEl) this.totalEl.textContent = formatMoney(cents);
    if (this.countEl) this.countEl.textContent = sel.length === 1 ? '1 producto seleccionado' : `${sel.length} productos seleccionados`;
    if (this.button) this.button.disabled = sel.length === 0;
  }

  setBusy(busy) {
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.querySelector('.dt-fbt__add-text')?.toggleAttribute('hidden', busy);
    this.button.querySelector('.dt-fbt__add-busy')?.toggleAttribute('hidden', !busy);
  }

  showMessage(text, isError = false) {
    if (!this.msg) return;
    this.msg.textContent = text;
    this.msg.classList.toggle('dt-fbt__msg--error', isError);
  }

  async add() {
    const sel = this.selected;
    if (!sel.length) return;
    const items = sel.map((b) => ({ id: Number(b.dataset.variantId), quantity: 1 }));
    const lines = sel.map((b) => ({ merchandiseId: String(b.dataset.variantId), quantity: 1 }));
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    this.setBusy(true);
    this.showMessage('');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      if (response.status) {
        const error = response.message || response.description || 'No se pudo agregar al carro';
        this.dispatchEvent(new CartErrorEvent({ error, code: 'INVALID', detail: { description: response.description, errors: response.errors } }));
        deferred.reject(new Error(error));
        this.showMessage(error, true);
        return;
      }

      const cartItems = document.querySelector('cart-items-component');
      const ajaxCart = cartItems && typeof cartItems.fetchCartData === 'function'
        ? await cartItems.fetchCartData()
        : await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'dt-comprados-juntos',
          sourceId: this.id,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      this.showMessage(items.length === 1 ? 'Producto agregado al carro.' : `${items.length} productos agregados al carro.`);
      if (window.dataLayer) window.dataLayer.push({ event: 'bundle_add_to_cart', items: items.length, value: sel.reduce((s, b) => s + Number(b.dataset.price || 0), 0) / 100 });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Error de red', code: 'SERVICE_UNAVAILABLE' }));
      this.showMessage('No se pudo agregar al carro. Intenta de nuevo.', true);
    } finally {
      this.setBusy(false);
      this.update();
    }
  }
}

if (!customElements.get('dt-comprados-juntos')) customElements.define('dt-comprados-juntos', DtCompradosJuntos);
